import { defineAsyncComponent } from 'vue'

import { DashboardChartArea } from '@/features/dashboards'
import type {
  ReportFilter,
  ReportLayoutConfig,
  ReportSectionTabConfig,
  SectionBuild,
  SectionDataVisibleConfig,
} from '@/features/reports'

// v3 `configs/MarketingInsights/PerformanceMaxCampaigns.js` (282 ln). One shared `getPmaxConfig`
// served every section; its per-tab inputs live in PMAX_TAB_DATA keyed by machine tab name.
// v3's id template `26-${tab.reportId}${name || tab.name}` received the (always-truthy, empty)
// dropdownValues array as `name` at the bare `config: getPmaxConfig` call sites, so runtime ids
// collapsed to `26-<reportId>` — ported verbatim (Image/Video/Text Assets share id '26-13'
// exactly as v3 did; only the Overview wrapper passed a real 'overview-channel' suffix).
// The `visibleType: tab.visibleType` passthrough resolves per-tab: only Placements declared
// 'double'. `hideCols` waits for ORP-18643 column shaping; the Overview trendline
// (`dashboardConfig` → module 09 DashboardChartArea) is wired below; Asset Quality is
// feature-owned below.

const GOOGLE_ADS_CHANNEL_FILTER: ReportFilter = {
  field: 'marketing_channel',
  match_mode: 'equals',
  value: 'google_ads',
}

const PMAX_CAMPAIGN_TYPE_FILTER: ReportFilter = {
  field: 'campaign_type',
  match_mode: 'in',
  value: ['PERFORMANCE_MAX', 'Performance Max'],
}

interface PmaxTabData {
  reportId: number
  partitionOnly?: boolean
  hideCols?: string[]
  filters?: ReportFilter[]
  sortField?: string
  breakdown?: string
  /** v3 `visibleType: tab.visibleType` passthrough — only Placements declared 'double'. */
  view?: SectionDataVisibleConfig['view']
  allowedViews?: SectionDataVisibleConfig['allowedViews']
  composition?: SectionDataVisibleConfig['composition']
  chartDimensions?: string[]
  chartMetrics?: string[]
}

const PMAX_TAB_DATA: Record<string, PmaxTabData> = {
  'Search Terms': {
    reportId: 5,
    partitionOnly: true,
    hideCols: ['adgroup_name', 'keyword'],
    filters: [
      GOOGLE_ADS_CHANNEL_FILTER,
      {
        field: 'searchterm_match_source',
        match_mode: 'in',
        value: ['PERFORMANCE_MAX', 'Performance Max'],
      },
    ],
  },
  'Channel Breakdown': {
    reportId: 26,
    sortField: 'cost',
    breakdown: 'ad_network_type',
  },
  'Landing Page': {
    reportId: 22,
    filters: [PMAX_CAMPAIGN_TYPE_FILTER],
  },
  'Image Assets': {
    reportId: 13,
    partitionOnly: true,
    filters: [GOOGLE_ADS_CHANNEL_FILTER, PMAX_CAMPAIGN_TYPE_FILTER],
  },
  'Video Assets': {
    reportId: 13,
    partitionOnly: true,
    filters: [GOOGLE_ADS_CHANNEL_FILTER, PMAX_CAMPAIGN_TYPE_FILTER],
  },
  'Text Assets': {
    reportId: 13,
    partitionOnly: true,
    filters: [GOOGLE_ADS_CHANNEL_FILTER, PMAX_CAMPAIGN_TYPE_FILTER],
  },
  'Product Assets': {
    reportId: 17,
    partitionOnly: true,
    filters: [GOOGLE_ADS_CHANNEL_FILTER, PMAX_CAMPAIGN_TYPE_FILTER],
  },
  Placements: {
    reportId: 26,
    sortField: 'impressions',
    view: 'table',
    composition: 'chart-table',
    allowedViews: ['table', 'chart'],
    chartDimensions: ['placement_type'],
    chartMetrics: ['impressions'],
  },
  'Asset Groups': {
    reportId: 68,
    filters: [GOOGLE_ADS_CHANNEL_FILTER, PMAX_CAMPAIGN_TYPE_FILTER],
  },
}

const buildPmax = (tab: ReportSectionTabConfig, idSuffix: string): SectionDataVisibleConfig => {
  const data = PMAX_TAB_DATA[tab.name] ?? { reportId: tab.reportId ?? 0 }
  return {
    id: `26-${data.reportId}${idSuffix}`,
    reportId: data.reportId,
    scheduleTabs: tab.tabs ?? [],
    filters: data.filters ?? [],
    ...(data.partitionOnly ? { partitionOnly: true } : {}),
    ...(data.hideCols ? { hideCols: data.hideCols } : {}),
    ...(data.sortField ? { sortField: data.sortField } : {}),
    ...(data.breakdown ? { breakdown: data.breakdown } : {}),
    ...(data.view ? { view: data.view } : {}),
    ...(data.allowedViews ? { allowedViews: data.allowedViews } : {}),
    ...(data.composition ? { composition: data.composition } : {}),
    ...(data.chartDimensions ? { chartDimensions: data.chartDimensions } : {}),
    ...(data.chartMetrics ? { chartMetrics: data.chartMetrics } : {}),
  }
}

const pmaxBuild: SectionBuild = ({ tab }) => buildPmax(tab, '')

const pmaxOverviewChannelBuild: SectionBuild = ({ tab }) => buildPmax(tab, 'overview-channel')

// v3 `dashboardConfig` → module 09 DashboardChartArea.
const pmaxOverviewTrendlineBuild: SectionBuild = ({ dropdownFilters }) => ({
  id: '26-overview',
  reportId: 6,
  scheduleTabs: ['overview'],
  filters: [GOOGLE_ADS_CHANNEL_FILTER, PMAX_CAMPAIGN_TYPE_FILTER, ...dropdownFilters],
})

export const PERFORMANCE_MAX_CAMPAIGNS: ReportLayoutConfig = [
  {
    name: 'Overview',
    labelKey: 'reports.performanceMaxCampaigns.tabs.overview',
    sections: [
      {
        id: '26-overview',
        renderer: DashboardChartArea,
        rendererMode: 'replace',
        rendererProps: {
          defaultMetricFields: ['cost', 'cpc', 'purchase_value', 'roas'],
          calcRoasFirstMetric: 'conversions_value',
          hideDatePicker: true,
        },
        build: pmaxOverviewTrendlineBuild,
        reportId: 6,
        hideChannels: true,
        tabs: [{ name: 'Performance Trendline', tabs: ['overview'] }],
      },
      {
        id: '26-overview-channel-landing-search',
        topTabMode: true,
        build: pmaxOverviewChannelBuild,
        tabs: [
          {
            name: 'Search Terms',
            labelKey: 'reports.performanceMaxCampaigns.overview.searchTerms',
            tabs: ['google_ads_searchterm'],
            reportId: 5,
            channel: 'google',
          },
          {
            name: 'Channel Breakdown',
            labelKey: 'reports.performanceMaxCampaigns.overview.channelBreakdown',
            tabs: ['performancemax_channel_breakdown'],
            reportId: 26,
          },
          {
            name: 'Landing Page',
            labelKey: 'reports.performanceMaxCampaigns.overview.landingPage',
            tabs: ['google_ads_landingpage'],
            reportId: 22,
            channel: 'google',
          },
        ],
      },
    ],
  },
  {
    name: 'Creatives',
    labelKey: 'reports.performanceMaxCampaigns.tabs.creatives',
    sections: [
      {
        id: '26-creatives',
        reportId: 13,
        topTabMode: true,
        build: pmaxBuild,
        tabs: [
          {
            name: 'Image Assets',
            labelKey: 'reports.performanceMaxCampaigns.creatives.imageAssets',
            tabs: ['google_ads_image'],
            reportId: 13,
            channel: 'google',
          },
          {
            name: 'Video Assets',
            labelKey: 'reports.performanceMaxCampaigns.creatives.videoAssets',
            tabs: ['google_ads_video'],
            reportId: 13,
            channel: 'google',
          },
          {
            name: 'Text Assets',
            labelKey: 'reports.performanceMaxCampaigns.creatives.textAssets',
            tabs: ['google_ads_adtext'],
            reportId: 13,
            channel: 'google',
          },
        ],
      },
    ],
  },
  {
    name: 'Products',
    labelKey: 'reports.performanceMaxCampaigns.tabs.products',
    sections: [
      {
        id: '26-products',
        titleKey: 'reports.performanceMaxCampaigns.products.title',
        reportId: 17,
        hideChannels: true,
        build: pmaxBuild,
        tabs: [
          {
            name: 'Product Assets',
            tabs: ['google_ads_product'],
            reportId: 17,
            channel: 'google',
          },
        ],
      },
    ],
  },
  {
    name: 'Placements',
    labelKey: 'reports.performanceMaxCampaigns.tabs.placements',
    sections: [
      {
        id: '26-placements',
        titleKey: 'reports.performanceMaxCampaigns.placements.title',
        reportId: 26,
        hideChannels: true,
        build: pmaxBuild,
        tabs: [
          {
            name: 'Placements',
            tabs: ['performance_max_placement'],
            reportId: 26,
            channel: 'google',
          },
        ],
      },
    ],
  },
  {
    name: 'Asset',
    labelKey: 'reports.performanceMaxCampaigns.tabs.asset',
    sections: [
      {
        // v3 section carried only `name: 'Asset Quality'` (no id); v4 requires one — synthesized
        // to match the sibling '26-asset-groups' convention.
        id: '26-asset-quality',
        titleKey: 'reports.performanceMaxCampaigns.assetQuality.title',
        reportId: 26,
        hideChannels: true,
        component: defineAsyncComponent(
          () => import('../components/sections/PmaxAssetQualityTable.vue'),
        ),
        tabs: [{ name: 'Asset Quality', tabs: ['googleads_assetgroup_assets'], reportId: 26 }],
      },
      {
        id: '26-asset-groups',
        // v3's heading came from the title-only tab (`{title: 'Asset Groups'}`) — hoisted here;
        // the tab keeps 'Asset Groups' as its machine name (rail is hidden) for the data map.
        titleKey: 'reports.performanceMaxCampaigns.assetGroups.title',
        reportId: 68,
        hideChannels: true,
        build: pmaxBuild,
        tabs: [
          {
            name: 'Asset Groups',
            tabs: ['google_ads_adgroup'],
            reportId: 68,
            channel: 'google',
          },
        ],
      },
    ],
  },
]
